import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { z } from 'zod';
import Form, { FORM_INPUT_TYPES } from 'new-ui/Components/Form';
import { PopupFooter } from 'new-ui/Components/Popup';
import Button from 'new-ui/Components/Button';
import { mapValidationToInputHelpers } from 'functions/mapValidationToInputHelpers';
import { useTranslation } from 'utils/modifiedTranslation';
import { useIsMounted } from 'utils/hooks/useIsMounted';
import './userSignupForm.css';

const ADD_PATIENT_FORM_ID = 'user-signup-form';

const getFormSchema = ({ emailRequired, t }) => {
  const emailBaseSchema = z.email(t('general.email_invalid_format')).nonempty(t('general.input_required'));

  return z.object({
    indication: z.object({
      value: z.string(),
      type: z.nullish(z.string()),
    }, t('general.input_required')),
    name: z
      .string()
      .min(2, t('general.input_too_short'))
      .max(100, t('general.input_too_long'))
      .nonempty(t('general.input_required')),
    email: emailRequired ? emailBaseSchema : emailBaseSchema.optional().or(z.literal('')),
  });
};

const getInputs = ({
  emailRequired, indicationTitle, nameTitle, emailTitle, indication,
}) => ({
  indication: {
    required: true,
    title: indicationTitle,
    type: FORM_INPUT_TYPES.SEARCH_INDICATION_CHOOSER,
    hidden: !!indication?.value,
  },
  name: {
    required: true,
    title: nameTitle,
    type: FORM_INPUT_TYPES.INPUT,
  },
  email: {
    title: emailTitle,
    type: FORM_INPUT_TYPES.INPUT,
    email: true,
    required: emailRequired,
  },
});

export const UserSignupForm = ({
  onSubmit,
  recaptcha,
  error,
  indication,
  name = '',
  email = '',
  emailRequired,
  indicationTitle,
  nameTitle,
  emailTitle,
  buttonTitle,
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    inputs: getInputs({
      emailRequired, indicationTitle, nameTitle, emailTitle, indication,
    }),
    data: {
      indication,
      name,
      email,
    },
  });
  const [formErrors, setFormErrors] = useState({});

  const handleSubmit = async () => {
    const { data } = form;
    setForm((state) => ({ ...state, data }));

    const validation = getFormSchema({ emailRequired, t }).safeParse(data);
    if (!validation.success) {
      setFormErrors(mapValidationToInputHelpers(validation));
      return;
    }
    setFormErrors({});
    try {
      setIsSubmitting(true);
      await onSubmit(form?.data);
    } finally {
      if (isMounted()) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="user-signup-form">
      {recaptcha}
      <div className="user-signup-form-title-holder">
        <div className="user-signup-form-title">
          {title}
        </div>
        {subtitle ? <div className="user-signup-form-subtitle">{subtitle}</div> : null}
      </div>
      <Form id={ADD_PATIENT_FORM_ID} form={form} inputHelperText={formErrors} />
      {error ? <div className="error">{error}</div> : null}
      <PopupFooter>
        <Button
          type="submit"
          form={ADD_PATIENT_FORM_ID}
          title={buttonTitle}
          action={!isSubmitting ? handleSubmit : null}
        />
      </PopupFooter>
    </div>
  );
};

UserSignupForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  recaptcha: PropTypes.node.isRequired,
  error: PropTypes.string,
  indication: PropTypes.shape({
    value: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  buttonTitle: PropTypes.string.isRequired,
  indicationTitle: PropTypes.string.isRequired,
  nameTitle: PropTypes.string.isRequired,
  emailTitle: PropTypes.string.isRequired,
  name: PropTypes.string,
  email: PropTypes.string,
  emailRequired: PropTypes.bool,
};
